import { ChevronRight } from "lucide-react";
import { useState, type ReactNode } from "react";

import { cn } from "@/lib/utils";

/**
 * A titled group of controls.
 *
 * The panel's problem was never that it did too much — it was that eleven
 * actions sat in one undifferentiated 2-column grid at identical visual weight,
 * so "Read this document" and "Delete it" looked like the same kind of thing and
 * nothing told you what the panel was FOR. Grouping is the fix, and grouping
 * needs a container that makes a group look like a group.
 */
export function Section({
  title,
  children,
  /** Sections that are occasionally useful start closed, so the panel opens on
   * the things people actually came for. */
  collapsible = false,
  defaultOpen = true,
  action,
}: {
  title: string;
  children: ReactNode;
  collapsible?: boolean;
  defaultOpen?: boolean;
  action?: ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);
  const body = <div className="space-y-1 pt-2">{children}</div>;

  if (!collapsible) {
    return (
      <section className="space-y-0.5">
        <div className="flex items-center gap-1.5">
          <h3 className="font-mono text-[11px] uppercase tracking-widest text-muted-foreground">
            {title}
          </h3>
          {action && <div className="ml-auto">{action}</div>}
        </div>
        {body}
      </section>
    );
  }

  return (
    <section className="space-y-0.5">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="flex w-full items-center gap-1.5 py-1 text-left font-mono text-[11px] uppercase tracking-widest text-muted-foreground transition-colors hover:text-foreground"
      >
        <ChevronRight className={cn("h-3.5 w-3.5 transition-transform", open && "rotate-90")} />
        {title}
      </button>
      {open && body}
    </section>
  );
}

/** A hairline between sections — the panel's own idiom, not a new one. */
export function SectionDivider() {
  return <div className="h-px bg-border/70" />;
}
